import { createReducer, Action, on } from '@ngrx/store';

import { DocumentListActions } from './document-list.actions';
import { DocumentListState } from './document-list.state';
import { TransactionDocumentSource } from '../../features/document/models/uploaded-document.model';
import { DEFAULT_PAGINATION_INFO } from '../../features/shared/models/common/paginated-list.model';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';

export const initialState: DocumentListState = {
  loading: false,
  submitting: false,
  searchFilter: {
    source: [TransactionDocumentSource.Webapp],
  },
  documentHashMatches: [],
  paginationInfo: DEFAULT_PAGINATION_INFO,
  total: 0,
  sort: {
    active: 'createdAt',
    direction: 'desc',
  },
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      DocumentListActions.LoadList,
      (state, action): DocumentListState => ({
        ...state,
        loading: true,
        error: undefined,
        paginationInfo: DEFAULT_PAGINATION_INFO,
      })
    ),
    on(
      DocumentListActions.LoadListSuccess,
      (state, action): DocumentListState => ({
        ...state,
        loading: false,
        documents: action.documents,
        paginationInfo: action.paginationInfo,
      })
    ),
    on(
      DocumentListActions.LoadCountSuccess,
      (state, action): DocumentListState => ({ ...state, loading: false, total: action.count })
    ),
    on(
      DocumentListActions.LoadNextChunk,
      (state, action): DocumentListState => ({
        ...state,
        loading: !state.paginationInfo.isLastPage,
        error: undefined,
      })
    ),
    on(
      DocumentListActions.LoadNextChunkSuccess,
      (state, action): DocumentListState => ({
        ...state,
        loading: false,
        documents: [...state.documents, ...action.documents],
        paginationInfo: action.paginationInfo,
      })
    ),
    on(
      DocumentListActions.LoadDocumentHashLookupSuccess,
      (state, action): DocumentListState => ({
        ...state,
        documentHashMatches: action.documentHashMatches,
      })
    ),
    on(
      DocumentListActions.LoadListFail,
      DocumentListActions.LoadNextChunkFail,
      DocumentListActions.LoadDocumentHashLookupFail,
      (state, action): DocumentListState => ({ ...state, loading: false, error: action.error })
    ),
    on(
      DocumentListActions.Search,
      (state, action): DocumentListState => ({ ...state, search: action.search })
    ),
    on(
      DocumentListActions.DeleteItemSuccess,
      (state, action): DocumentListState => ({
        ...state,
        documents: state.documents?.filter((d) => d.id !== action.document.id),
        selectedDocumentId:
          state.selectedDocumentId === action.document.id ? undefined : state.selectedDocumentId,
      })
    ),
    on(
      DocumentListActions.SaveDocument,
      (state, action): DocumentListState => ({ ...state, submitting: true })
    ),
    on(
      DocumentListActions.SaveDocumentFail,
      (state, action): DocumentListState => ({ ...state, submitting: false, error: action.error })
    ),
    on(
      DocumentListActions.SaveDocumentSuccess,
      (state, action): DocumentListState => ({
        ...state,
        submitting: false,
        documents: state.documents?.map((d) =>
          d.id === action.document.id
            ? {
                ...d,
                status: action.document?.status,
                internalStatus: action.document?.internalStatus,
                transactionId: action.document?.transactionId,
                responsible: action.document?.responsible,
                hasIncomingDocument: action.document?.hasIncomingDocument,
                statusReason: action.document?.statusReason,
              }
            : d
        ),
      })
    ),
    on(
      DocumentListActions.Sort,
      (state, action): DocumentListState => ({ ...state, sort: action.sort })
    ),
    on(
      DocumentListActions.Filter,
      (state, action): DocumentListState => ({ ...state, searchFilter: action.filter })
    ),
    on(
      DocumentListActions.SelectDocument,
      (state, action): DocumentListState => ({
        ...state,
        selectedDocumentId: action.selectedDocumentId,
      })
    ),
    on(
      DocumentListActions.LockDocuments,
      (state, action): DocumentListState => ({
        ...state,
        lockedDocumentIds: action.lockedDocumentIds,
      })
    ),
    on(
      DocumentListActions.UpdateCrudOptions,
      (state, action): DocumentListState => ({
        ...state,
        searchFilter: action.options.searchFilter,
        sort: action.options.sort,
        search: action.options.search,
      })
    )
  )
);

export function reducer(state: DocumentListState | undefined, action: Action) {
  return featureReducer(state, action);
}
